import type { PermissionRequestSummary } from '@gestion-permisos/contracts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { addMonths, DAY_SHORT, dayNumber, longCivilDate, monthGrid, monthOf, monthTitle } from '../requests/civil-dates.js';
import { STATUS_LABELS, todayCivil } from '../requests/format.js';
import { useTeamCalendarRequests } from '../requests/requests-api.js';
import { StatusBadge } from '../requests/status-badge.js';

/** Solicitudes visibles por día: 3 en escritorio y 1 en un ancho menor; el resto va en «+N más». */
const DESKTOP_VISIBLE = 3;
const MOBILE_VISIBLE = 1;

/**
 * Mes visible del calendario del equipo y su lectura (`from` y `to` son el primer y el último día de la
 * rejilla). El mes vive en el estado de la pantalla, no en la URL: cambiarlo no toca la lista ni sus filtros.
 * «Hoy» es la fecha civil de America/Bogota.
 */
export function useTeamCalendar() {
  const today = todayCivil();
  const [month, setMonth] = useState(() => monthOf(today));
  const days = monthGrid(month);
  const result = useTeamCalendarRequests(days[0]!, days[days.length - 1]!);
  return { today, month, setMonth, days, result };
}

export type TeamCalendarState = ReturnType<typeof useTeamCalendar>;

/**
 * Calendario del equipo, con las funciones del de la Power App (`src/components/team-calendar.tsx`): mes con
 * semana de lunes a domingo, días vecinos atenuados, hoy en verde y cada solicitud enlazada a su detalle.
 * No obedece los filtros de la lista: muestra todas las solicitudes del equipo directo en los días visibles.
 */
export function TeamCalendar({ state }: { state: TeamCalendarState }) {
  const { today, month, setMonth, days, result } = state;
  const byDate = new Map<string, PermissionRequestSummary[]>();
  for (const request of result.data ?? []) {
    const list = byDate.get(request.permissionDate);
    if (list) list.push(request);
    else byDate.set(request.permissionDate, [request]);
  }
  const title = monthTitle(month);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle>Calendario del equipo</CardTitle>
            {result.isSuccess && result.data.length === 0 ? <p className="text-sm text-muted-foreground">No hay solicitudes de tu equipo en este mes.</p> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={buttonClasses('outline')} aria-label="Mes anterior" onClick={() => setMonth(addMonths(month, -1))}>
              <ChevronLeft aria-hidden className="size-4" />
            </button>
            <p className="min-w-36 text-center text-sm font-medium" aria-live="polite">
              {title}
            </p>
            <button type="button" className={buttonClasses('outline')} aria-label="Mes siguiente" onClick={() => setMonth(addMonths(month, 1))}>
              <ChevronRight aria-hidden className="size-4" />
            </button>
            <button type="button" className={buttonClasses('outline')} onClick={() => setMonth(monthOf(today))}>
              Hoy
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {result.isPending ? <output className="block text-muted-foreground">Cargando el calendario…</output> : null}
        {result.isError ? <ApiErrorView error={result.error} /> : null}
        {result.isSuccess ? (
          <div className="overflow-x-auto">
            <div className="min-w-[28rem] overflow-hidden rounded-xl border border-border bg-border">
              <div aria-hidden="true" className="grid grid-cols-7 gap-px">
                {DAY_SHORT.map((label) => (
                  <div key={label} className="bg-muted px-1 py-2 text-center text-[10px] font-medium text-muted-foreground sm:text-xs">
                    {label}
                  </div>
                ))}
              </div>
              <ol aria-label={`Días de ${title}`} className="mt-px grid grid-cols-7 gap-px">
                {days.map((day) => (
                  <CalendarDay key={day} day={day} inMonth={monthOf(day) === month} isToday={day === today} requests={byDate.get(day) ?? []} />
                ))}
              </ol>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CalendarDay({ day, inMonth, isToday, requests }: { day: string; inMonth: boolean; isToday: boolean; requests: PermissionRequestSummary[] }) {
  const tone = inMonth ? 'bg-card' : 'bg-muted/40 text-muted-foreground';
  return (
    <li aria-current={isToday ? 'date' : undefined} className={`min-h-20 min-w-0 p-1 sm:min-h-28 sm:p-1.5 ${tone} ${isToday ? 'ring-1 ring-inset ring-primary' : ''}`}>
      <p className="mb-1">
        <span
          aria-hidden="true"
          className={`inline-flex size-6 items-center justify-center rounded-full text-xs font-medium ${isToday ? 'bg-primary text-primary-foreground' : ''}`}
        >
          {dayNumber(day)}
        </span>
        <span className="sr-only">{longCivilDate(day)}</span>
      </p>
      {requests.length > 0 ? (
        <ul className="space-y-1">
          {requests.slice(0, DESKTOP_VISIBLE).map((request, index) => (
            // A partir de la segunda, solo en escritorio.
            <li key={request.id} className={index < MOBILE_VISIBLE ? '' : 'hidden md:block'}>
              <EventLink request={request} />
            </li>
          ))}
        </ul>
      ) : null}
      {requests.length > MOBILE_VISIBLE ? <DayOverflow day={day} requests={requests.slice(MOBILE_VISIBLE)} className="md:hidden" /> : null}
      {requests.length > DESKTOP_VISIBLE ? <DayOverflow day={day} requests={requests.slice(DESKTOP_VISIBLE)} className="hidden md:block" /> : null}
    </li>
  );
}

const schedule = (request: PermissionRequestSummary) => `${request.departureTime}–${request.returnTime}`;
const eventLabel = (request: PermissionRequestSummary) =>
  `Solicitud de ${request.employee.fullName}, ${request.permissionType.name}, de ${request.departureTime} a ${request.returnTime}`;
/** En la lista de «+N más» también se lee el estado, que se ve en la insignia. */
const overflowLabel = (request: PermissionRequestSummary) => `${eventLabel(request)}, ${STATUS_LABELS[request.status]}`;

/** Una solicitud en la celda: nombre, tipo y horario tal como llega; en una celda estrecha el horario se oculta. */
function EventLink({ request }: { request: PermissionRequestSummary }) {
  return (
    <Link
      to={`/solicitudes/${request.id}`}
      aria-label={eventLabel(request)}
      className="block rounded-md border border-border bg-card px-1.5 py-1 text-left text-card-foreground transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <span className="block truncate text-[10px] leading-tight font-medium sm:text-xs">{request.employee.fullName}</span>
      <span className="block truncate text-[10px] leading-tight text-muted-foreground sm:text-xs">{request.permissionType.name}</span>
      <span className="mt-0.5 hidden truncate text-[10px] leading-tight text-muted-foreground md:block">{schedule(request)}</span>
    </Link>
  );
}

/**
 * «+N más»: abre la lista de las demás solicitudes del día con nombre, tipo, horario y estado. Es un panel con el
 * atributo `popover` (capa superior, sin recortes por el desplazamiento de la tarjeta) que se cierra con
 * «Cerrar», con Escape o con el mismo botón; donde el navegador no admite `popover`, se despliega en la celda.
 */
const supportsPopover = () => typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';

function DayOverflow({ day, requests, className }: { day: string; requests: PermissionRequestSummary[]; className: string }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const element = panel.current;
    if (!open || !element) return;
    if (supportsPopover()) element.showPopover();
    element.focus();
  }, [open]);

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  return (
    <div className={className}>
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
        className="mt-1 w-full rounded-md px-1.5 py-0.5 text-left text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:text-xs"
      >
        +{requests.length} más
      </button>
      {open ? (
        <div
          ref={panel}
          id={panelId}
          popover={supportsPopover() ? 'manual' : undefined}
          role="dialog"
          aria-label={`Más solicitudes del ${longCivilDate(day)}`}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="m-auto w-72 max-w-[calc(100vw-2rem)] space-y-2 rounded-xl border border-border bg-card p-3 text-card-foreground shadow-lg"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-muted-foreground">{longCivilDate(day)}</p>
            <button type="button" className={buttonClasses('ghost', 'h-7 px-2 text-xs')} onClick={close}>
              Cerrar
            </button>
          </div>
          <ul className="max-h-64 space-y-2 overflow-y-auto">
            {requests.map((request) => (
              <li key={request.id}>
                <Link
                  to={`/solicitudes/${request.id}`}
                  aria-label={overflowLabel(request)}
                  className="flex items-start justify-between gap-2 rounded-lg border border-border p-2 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{request.employee.fullName}</span>
                    <span className="block truncate text-xs text-muted-foreground">{request.permissionType.name}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">{schedule(request)}</span>
                  </span>
                  <StatusBadge status={request.status} tokens />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
